import { Controller } from "@hotwired/stimulus"
import {
  libAllSorted, libGet, libUpdate, libDelete, libAddNewGoal,
  libFamilyForSerial, libFamilyVendorsResolved, libFamilySet, xxxOfSerial,
} from "goal_library"
import { vendorGet } from "vendor_library"
import { goalEditorHtml, esc, refRowHtml, vendorRowHtml } from "goal_editor_view"

// Settings → Goals manager: lists the user's LOCAL goal library (serial PCP
// XXX.YY) and lets them add / edit / delete goals. The library is stored locally
// (PCPDesk: OneDrive-synced SQLite via window.__PCP_LIBRARY__; plain browser:
// localStorage) — NOT on the server — so this reads/writes it client-side.
//
// References (DDS directives / citations) and a candidate Vendor list belong to
// the GOAL (XXX), shared across every variation (YY) — they are edited here too
// and stored per-family via libFamilySet.
const CATEGORIES = {
  adaptive: "Adaptive Skills", socialrec: "Social Rec", nonmedical: "Non-Medical",
  educational: "Educational", camp: "Camp", therapy: "Clinical Therapies",
  parenttraining: "Parent Training", living: "Daily Living & Family",
  coordination: "Coordination", access: "Access", employment: "Employment & Day",
  residential: "Residential", other: "Other",
}

export default class extends Controller {
  static targets = ["list", "empty", "count", "editor", "addBtn"]

  connect() {
    this.render()
    // Watch for section visibility changes (when left-rail nav switches sections).
    // If the goals card is hidden while the editor is open, close it so no stale
    // editor content appears when the user returns to Goals.
    this._observer = new MutationObserver(() => {
      if (this.element.hidden && this.hasEditorTarget && !this.editorTarget.hidden) {
        this._closeEditor()
      }
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["hidden"] })
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
  }

  render() {
    const goals = libAllSorted()
    if (this.hasCountTarget) this.countTarget.textContent = goals.length ? `${goals.length} goal${goals.length === 1 ? "" : "s"}` : ""
    if (this.hasEmptyTarget) this.emptyTarget.hidden = goals.length > 0
    if (this.hasListTarget) this.listTarget.innerHTML = goals.map(g => this._rowHtml(g)).join("")
  }

  add() {
    this._openEditor({ name: "", category: "socialrec", timeline: "12 months", domain: "", description: "" }, null)
  }

  edit(event) {
    const serial = event.currentTarget.dataset.serial
    const g = libGet(serial)
    if (g) this._openEditor(g, serial)
  }

  remove(event) {
    const serial = event.currentTarget.dataset.serial
    if (serial && window.confirm(`Delete library goal ${serial}? This can't be undone.`)) { libDelete(serial); this.render() }
  }

  // ---- rendering ----

  _esc(v) { return esc(v) }

  _rowHtml(g) {
    const cat = CATEGORIES[g.category] || g.category || ""
    const meta = [cat, g.timeline].filter(Boolean).map(v => this._esc(v)).join(" · ")
    const fam = libFamilyForSerial(g.serial)
    const pill = (n, label, color) => n ? `<span class="goal-editor__pill goal-editor__pill--${color}">${n} ${label}${n === 1 ? "" : "s"}</span>` : ""
    const chips = [pill(fam.references.length, "reference", "blue"), pill(fam.vendors.length, "vendor", "purple")].filter(Boolean).join(" ")
    return `
      <div class="goal-editor__list-row">
        <div class="goal-editor__list-row-header">
          <span class="goal-editor__serial-chip">${this._esc(g.serial)}</span>
          <strong class="goal-editor__list-name">${this._esc(g.name || "(untitled)")}</strong>
          ${meta ? `<span class="goal-editor__list-meta">${meta}</span>` : ""}
          <span class="goal-editor__list-actions">
            <button type="button" class="goal-editor__edit-btn" data-action="settings-goals#edit" data-serial="${this._esc(g.serial)}">Edit</button>
            <button type="button" class="goal-editor__delete-btn" data-action="settings-goals#remove" data-serial="${this._esc(g.serial)}">Delete</button>
          </span>
        </div>
        ${g.description ? `<div class="goal-editor__list-desc">${this._esc(g.description)}</div>` : ""}
        ${chips ? `<div class="goal-editor__list-chips">${chips}</div>` : ""}
      </div>`
  }

  // ---- full-space inline editor ----

  _openEditor(g, serial) {
    const isNew = !serial

    // Family data (references / vendors) — shared across variations.
    const fam = isNew ? { references: [], vendors: [] } : libFamilyForSerial(serial)
    const refs = fam.references.map(r => ({ id: r.id, chip: r.chip || "", title: r.title || "", url: r.url || "" }))

    // Vendors are now { vendorId } refs — resolve to full objects for display.
    // Store vendorIds as the source of truth; resolved objects are for rendering.
    const xxx = isNew ? null : xxxOfSerial(serial)
    const vendorIds = fam.vendors.map(v => v.vendorId).filter(Boolean)
    const resolveVendors = () => vendorIds.map(id => vendorGet(id)).filter(v => v != null)

    // Collision-proof temp ids for new rows
    let rid = 1
    const nid = () => `n${Date.now().toString(36)}${(rid++).toString(36)}${Math.random().toString(36).slice(2, 6)}`

    // Generate HTML using the pure module (pass resolved vendors for display)
    const html = goalEditorHtml({
      goal: g,
      serial,
      categories: CATEGORIES,
      refs,
      vendors: resolveVendors(),
    })

    // Hide list/empty/add button, show editor
    if (this.hasListTarget) this.listTarget.hidden = true
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = true

    this.editorTarget.hidden = false
    this.editorTarget.innerHTML = html

    const editor = this.editorTarget.querySelector(".goal-editor")

    // --- list editors (references / vendors) ---
    const renderRefs = () => {
      const box = editor.querySelector('[data-list="refs"]')
      box.innerHTML = refs.length ? refs.map(refRowHtml).join("") : `<div class="goal-editor__empty">None yet.</div>`
    }

    const renderVendors = () => {
      const box = editor.querySelector('[data-list="vendors"]')
      const resolved = resolveVendors()
      box.innerHTML = resolved.length ? resolved.map(vendorRowHtml).join("") : ""
    }

    // Pull current DOM values back into the refs array (vendors no longer have input fields)
    const syncRefsFromDom = () => {
      const box = editor.querySelector('[data-list="refs"]')
      box.querySelectorAll("[data-row]").forEach(row => {
        const item = refs.find(x => String(x.id) === String(row.dataset.row))
        if (!item) return
        row.querySelectorAll("[data-c]").forEach(inp => { item[inp.dataset.c] = inp.value })
      })
    }

    // Add reference buttons
    editor.querySelectorAll('[data-add="refs"]').forEach(btn => btn.addEventListener("click", () => {
      syncRefsFromDom()
      refs.push({ id: nid(), chip: "", title: "", url: "" })
      renderRefs()
    }))

    // Delete buttons (works for both refs and vendors)
    editor.addEventListener("click", (e) => {
      const del = e.target.closest("[data-del]")
      if (!del) return
      const kind = del.dataset.del
      const row = del.closest("[data-row]")
      const rowId = row.dataset.row
      if (kind === "refs") {
        syncRefsFromDom()
        const i = refs.findIndex(x => String(x.id) === rowId)
        if (i >= 0) refs.splice(i, 1)
        renderRefs()
      } else if (kind === "vendors") {
        // Remove vendor by id from vendorIds array
        const i = vendorIds.indexOf(rowId)
        if (i >= 0) vendorIds.splice(i, 1)
        renderVendors()
      }
    })

    // Vendor picker integration — listen for selection events
    const pickerRow = editor.querySelector(".goal-editor__vendor-picker-row")
    if (pickerRow) {
      pickerRow.addEventListener("vendor-picker:change", (e) => {
        const { id } = e.detail
        if (id && !vendorIds.includes(id)) {
          vendorIds.push(id)
          renderVendors()
        }
        // Clear the picker after adding
        const ctrl = this.application.getControllerForElementAndIdentifier(pickerRow, "vendor-picker")
        if (ctrl) ctrl.clear()
      })
    }

    // --- save / cancel ---
    const val = (n) => { const e = editor.querySelector(`[data-f="${n}"]`); return e ? e.value : "" }
    const close = () => {
      document.removeEventListener("keydown", onKey)
      this._closeEditor()
    }
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); close() } }
    document.addEventListener("keydown", onKey)

    editor.querySelector('[data-role="cancel"]').addEventListener("click", close)
    editor.querySelector('[data-role="save"]').addEventListener("click", () => {
      const fields = { name: val("name").trim(), category: val("category"), domain: val("domain").trim(), timeline: val("timeline").trim(), description: val("description") }
      if (!fields.name) { const n = editor.querySelector('[data-f="name"]'); if (n) n.focus(); return }
      syncRefsFromDom()
      const cleanRefs = refs.map(r => ({ id: r.id, chip: r.chip.trim(), title: r.title.trim(), url: r.url.trim() })).filter(r => r.title || r.url)
      // Vendors are stored as { vendorId } references
      const cleanVendors = vendorIds.map(vendorId => ({ vendorId }))
      const outSerial = isNew ? libAddNewGoal(fields) : (libUpdate(serial, fields), serial)
      const outXxx = xxxOfSerial(outSerial)
      if (outXxx != null) libFamilySet(outXxx, { references: cleanRefs, vendors: cleanVendors })
      document.removeEventListener("keydown", onKey)
      this._closeEditor()
      this.render()
    })

    // Focus name input
    setTimeout(() => { const n = editor.querySelector('[data-f="name"]'); if (n) { n.focus(); n.select() } }, 0)
  }

  _closeEditor() {
    if (this.hasEditorTarget) {
      this.editorTarget.hidden = true
      this.editorTarget.innerHTML = ""
    }
    if (this.hasListTarget) this.listTarget.hidden = false
    if (this.hasEmptyTarget) {
      const goals = libAllSorted()
      this.emptyTarget.hidden = goals.length > 0
    }
    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = false
  }
}
